import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import type { DashboardSummary, RecentMovementRow } from './types';
import { SkeletonBlock, SkeletonRows } from '../skeleton';

/**
 * 仪表盘（roadmap M5 首版 + M8 补全 §30 剩余维度）。"库存总量"跨产品单位
 * 不可通约（U-05），显示批次数与产品数这类可通约指标，不编造一个没有
 * 单位语义的总数。Attention 卡片条（负库存/低库存/临期）由 M8 预警引擎
 * 的 alert 表驱动，点击跳转到预警中心对应分组。对账状态卡是 D-24 批准的
 * 漂移可见面。库存价值（R-11：单一本位币，Σ qty×unit_cost，成本缺失按 0
 * 计入）与预留数是 M8 补的两个纯展示指标。待批盘点在 M9 落地前恒为 0。
 */
export function DashboardPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => apiFetch<DashboardSummary>('/dashboard/summary'),
  });

  if (isPending)
    return (
      <div>
        <SkeletonBlock height={120} />
        <SkeletonBlock height={96} />
        <SkeletonRows rows={4} />
      </div>
    );
  if (!data) return <div className="empty-state">{t('empty.noResults')}</div>;

  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });
  const valueFmt = data.inventoryValue
    ? new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: data.inventoryValue.currency,
      })
    : null;

  const attentionCards: ReadonlyArray<{
    key: keyof DashboardSummary['attention'];
    labelKey: string;
    tone: 'danger' | 'warn' | 'info';
  }> = [
    { key: 'negativeStock', labelKey: 'dashboard.negativeStockCount', tone: 'danger' },
    { key: 'expired', labelKey: 'dashboard.expiredCount', tone: 'danger' },
    { key: 'expiryToday', labelKey: 'dashboard.expiryTodayCount', tone: 'warn' },
    { key: 'expiryNear', labelKey: 'dashboard.expiryNearCount', tone: 'warn' },
    { key: 'lowStock', labelKey: 'dashboard.lowStockCount', tone: 'info' },
  ];

  return (
    <div>
      <h1 style={{ fontSize: 'var(--text-2xl)', marginBottom: 'var(--space-4)' }}>
        {t('nav.dashboard')}
      </h1>

      <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>
        {t('dashboard.attentionTitle')}
      </h2>
      <div className="stat-grid">
        {attentionCards.map((card) => {
          const count = data.attention[card.key];
          return (
            <Link
              key={card.key}
              to="/alerts?group=needsAttention"
              className="card"
              style={{
                flex: 1,
                padding: 'var(--space-4)',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
                {count > 0 ? (
                  <span className={`badge badge--${card.tone}`}>{count}</span>
                ) : (
                  <span>0</span>
                )}
              </div>
              <div className="field-hint">{t(card.labelKey)}</div>
            </Link>
          );
        })}
      </div>

      <div className="stat-grid">
        <div className="card" style={{ flex: 1, padding: 'var(--space-4)' }}>
          <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>{data.productCount}</div>
          <div className="field-hint">{t('dashboard.productCount')}</div>
        </div>
        <div className="card" style={{ flex: 1, padding: 'var(--space-4)' }}>
          <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>{data.batchCount}</div>
          <div className="field-hint">{t('dashboard.batchCount')}</div>
        </div>
        <div className="card" style={{ flex: 1, padding: 'var(--space-4)' }}>
          <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
            {valueFmt ? valueFmt.format(Number(data.inventoryValue!.amount)) : '—'}
          </div>
          <div className="field-hint" title={t('dashboard.inventoryValueHint')}>
            {t('dashboard.inventoryValue')}
          </div>
        </div>
        <div className="card" style={{ flex: 1, padding: 'var(--space-4)' }}>
          <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
            {data.reservationsCount}
          </div>
          <div className="field-hint">{t('dashboard.reservationsCount')}</div>
        </div>
        <div className="card" style={{ flex: 1, padding: 'var(--space-4)' }}>
          <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
            {data.pendingStockCountApprovals}
          </div>
          <div className="field-hint" title={t('dashboard.pendingStockCountsHint')}>
            {t('dashboard.pendingStockCounts')}
          </div>
        </div>
        <div className="card" style={{ flex: 1, padding: 'var(--space-4)' }}>
          {data.reconciliation ? (
            <>
              <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
                {data.reconciliation.status === 'CLEAN' ? (
                  <span className="badge badge--success">{t('dashboard.reconClean')}</span>
                ) : (
                  <span className="badge badge--danger">
                    {t('dashboard.reconDrift', { count: data.reconciliation.driftCount })}
                  </span>
                )}
              </div>
              <div className="field-hint">
                {t('dashboard.reconLabel')} · {fmt.format(new Date(data.reconciliation.ranAt))}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>—</div>
              <div className="field-hint">{t('dashboard.reconNever')}</div>
            </>
          )}
        </div>
      </div>

      <RecentMovementSection
        titleKey="dashboard.recentInbound"
        emptyKey="dashboard.noInboundYet"
        rows={data.recentInbound}
        fmt={fmt}
      />
      <RecentMovementSection
        titleKey="dashboard.recentOutbound"
        emptyKey="dashboard.noOutboundYet"
        rows={data.recentOutbound}
        fmt={fmt}
      />
      <RecentMovementSection
        titleKey="dashboard.recentTransfer"
        emptyKey="dashboard.noTransferYet"
        rows={data.recentTransfer}
        fmt={fmt}
      />
    </div>
  );
}

function RecentMovementSection({
  titleKey,
  emptyKey,
  rows,
  fmt,
}: {
  readonly titleKey: string;
  readonly emptyKey: string;
  readonly rows: ReadonlyArray<RecentMovementRow>;
  readonly fmt: Intl.DateTimeFormat;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>{t(titleKey)}</h2>
      {rows.length === 0 ? (
        <div className="empty-state" style={{ marginBottom: 'var(--space-4)' }}>
          <p>{t(emptyKey)}</p>
          {titleKey === 'dashboard.recentInbound' && (
            <Link to="/inventory" className="btn btn--primary btn--sm">
              {t('inventory.inboundAction')}
            </Link>
          )}
        </div>
      ) : (
        <div className="table" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
          {rows.map((m) => (
            <div
              key={`${m.movementId}`}
              className="tree__row"
              style={{ padding: 'var(--space-2) 0' }}
            >
              <span style={{ width: '160px', color: 'var(--color-text-muted)' }}>
                {fmt.format(new Date(m.performedAt))}
              </span>
              <span style={{ width: '150px' }}>{m.docNo}</span>
              <span style={{ flex: 2 }}>{m.productName}</span>
              <span style={{ width: '140px' }}>{m.batchNo}</span>
              <span style={{ width: '100px', textAlign: 'right' }}>{m.qtyBase}</span>
              <span style={{ flex: 2, color: 'var(--color-text-muted)' }}>
                {m.pathLabel ?? '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
